import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { LeadershipPageData } from '../../types/api';

export const LeadershipPage: React.FC = () => {
    const [data, setData] = useState<LeadershipPageData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        api.getLeadership().then(res => {
            if (res.data) setData(res.data);
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, []);

    const experiences = data?.experiences || [];

    return (
        <div>
            {/* Header */}
            <section className="py-20 bg-radial-glow border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl space-y-4">
                        <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Executive Roles & Involvement</span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                            Leadership & Activities
                        </h1>
                        <p className="text-lg text-slate-300 leading-relaxed">
                            A verified chronological record of leadership responsibilities, financial stewardships, and youth community projects.
                        </p>
                    </div>
                </div>
            </section>

            {/* Timeline / Records Section */}
            <section className="py-20 bg-slate-950">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="relative border-l-2 border-emerald-500/30 pl-6 sm:pl-10 space-y-12 ml-4 sm:ml-8">
                        {experiences.length > 0 ? (
                            experiences.map(exp => (
                                <div key={exp.id} className="relative group">
                                    {/* Marker Dot */}
                                    <div className="absolute -left-[31px] sm:-left-[47px] top-1.5 w-5 h-5 rounded-full bg-slate-950 border-4 border-emerald-500 group-hover:scale-125 transition"></div>

                                    {/* Record Card */}
                                    <div className="p-6 sm:p-8 rounded-3xl glass-card glass-card-hover space-y-4">
                                        
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                {exp.period || 'Active Period'}
                                            </span>

                                            {exp.is_featured && (
                                                <span className="text-xs font-semibold text-amber-400 flex items-center gap-1">
                                                    ★ Featured Role
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <h2 className="text-2xl font-bold text-white tracking-tight">{exp.role_title}</h2>
                                            <h3 className="text-base font-semibold text-emerald-400 mt-0.5">{exp.organization_name}</h3>
                                        </div>

                                        <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                                            {exp.description}
                                        </p>

                                        {exp.organization_url && (
                                            <div className="pt-4 border-t border-slate-800/80">
                                                <a href={exp.organization_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400 hover:text-emerald-300 uppercase tracking-wider transition">
                                                    Official Organization Website &rarr;
                                                </a>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-16 text-slate-500">
                                {loading ? 'Loading leadership timeline...' : 'No leadership records currently available.'}
                            </div>
                        )}
                    </div>

                </div>
            </section>
        </div>
    );
};
